import DescriptionIcon from '@mui/icons-material/Description';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, IconButton, Menu, MenuItem, Stack, type Theme, Tooltip, Typography } from '@mui/material';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import DownloadPopup from '/@/components/bayer/download-popup';
import ChipLabel from '/@/components/chip-label';
import { ShareItemPopup } from '/@/components/share-item-popup';
import colors from '/@/components/theme-provider/colors';
import { useIsValidatedConsultant, useIsValidatedRV } from '/@/hooks/session';
import { useToggle } from '/@/hooks/toggle/useToggle';
import { useDeleteVisitReport } from '/@/hooks/visit-reports';
import { _ } from '/@/locale';
import { getSessionLanguage } from '/@/store/session/selectors';
import type { VisitReport } from '/@/types/field';
import { formatDate } from '/@/utils';
import { reverse } from '/@/utils/router';
import { generateVisitReportPDF } from '/@/views/visit-reports/result/hooks';

export const VisitReportRow = ({
    visitReport,
    simplifiedVersion = false,
}: {
    visitReport: VisitReport;
    simplifiedVersion?: boolean;
}) => {
    const params = useParams();
    const fieldId = Number(params.fieldId);
    const fieldObservation = visitReport.fieldObservations.find(
        (fieldObservation) => fieldObservation.field === fieldId
    );

    const language = useSelector(getSessionLanguage);
    const [rowActionsAnchorEl, setRowActionsAnchorEl] = useState<HTMLElement | null>(null);
    const navigate = useNavigate();
    const isRv = useIsValidatedRV();
    const isConsultant = useIsValidatedConsultant();
    const { open: openSharePopup, setOpen: setOpenSharePopup, setClose: setCloseSharePopup } = useToggle();
    const { open: openDownloadPopup, setOpen: setOpenDownloadPopup, setClose: setCloseDownloadPopup } = useToggle();
    const deleteVisitReportMutation = useDeleteVisitReport({ visitReportId: visitReport.id });

    const menuItems: {
        label: string;
        onClick: (e: React.MouseEvent<HTMLElement>) => void;
    }[] = [
        ...(visitReport.status === 'saved'
            ? [
                  {
                      label: _('Ver informe'),
                      onClick: (e) => {
                          navigate(
                              reverse('visitReport.VisitReportDetail', { params: { visitReportId: visitReport.id } })
                          );
                      },
                  },
              ]
            : []),
        ...(isRv || isConsultant
            ? [
                  {
                      label: _('Editar informe'),
                      onClick: (e) => {
                          navigate(
                              reverse('visitReport.VisitReportEdit', { params: { visitReportId: visitReport.id } })
                          );
                      },
                  },
              ]
            : []),
        {
            label: _('Compartir'),
            onClick: (e) => {
                setOpenSharePopup();
            },
        },
        {
            label: _('Descargar informe'),
            onClick: (e) => {
                setOpenDownloadPopup();
            },
        },
        ...(isRv || isConsultant
            ? [
                  {
                      label: _('Eliminar'),
                      onClick: (e) => {
                          deleteVisitReportMutation.trigger(e);
                      },
                  },
              ]
            : []),
    ];

    const handleClick = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        if (visitReport.status === 'draft') {
            navigate(
                reverse('visitReport.VisitReportEdit', {
                    params: { visitReportId: visitReport.id },
                })
            );
        } else {
            navigate(
                reverse('visitReport.VisitReportDetail', {
                    params: { visitReportId: visitReport.id },
                })
            );
        }
    };

    return (
        <Box
            px={simplifiedVersion ? 1 : 2}
            py={simplifiedVersion ? 0 : 1}
            borderRadius={2}
            border={1}
            borderColor='divider'
            sx={{ backgroundColor: colors.gray[200], width: { xs: '100%', sm: 'unset' } }}
            onClick={(e: React.MouseEvent<HTMLElement>) => {
                if (simplifiedVersion) {
                    handleClick(e);
                }
            }}
        >
            <Stack direction='row' spacing={1} alignItems='center' justifyContent='space-between'>
                <Stack direction='row' spacing={1} alignItems='center'>
                    <Typography fontWeight='bold' variant='body1' color='primary'>
                        {formatDate(new Date(visitReport.date))}
                    </Typography>
                    {fieldObservation?.vegetativeStage && (
                        <ChipLabel label={fieldObservation?.vegetativeStage} type='fallow' />
                    )}
                    <Tooltip title={visitReport.status === 'draft' && isRv ? _('Borrador guardado') : ''} arrow>
                        <IconButton
                            onClick={(e: React.MouseEvent<HTMLElement>) => {
                                handleClick(e);
                            }}
                            disabled={visitReport.status === 'draft' && !isRv}
                            size={simplifiedVersion ? 'small' : 'medium'}
                            sx={{ visibility: visitReport.status === 'draft' && !isRv ? 'hidden' : 'visible' }}
                        >
                            {visitReport.status === 'draft' ? (
                                <DriveFileRenameOutlineIcon
                                    sx={{
                                        color: (t: Theme) => t.palette.primary.onSurface,
                                        fontSize: simplifiedVersion ? 16 : 24,
                                    }}
                                />
                            ) : (
                                <DescriptionIcon
                                    sx={{
                                        color: (t: Theme) => t.palette.primary.onSurface,
                                        fontSize: simplifiedVersion ? 16 : 24,
                                    }}
                                />
                            )}
                        </IconButton>
                    </Tooltip>
                </Stack>

                <ShareItemPopup
                    visible={openSharePopup}
                    onClose={setCloseSharePopup}
                    itemType='visit-report'
                    item={visitReport}
                    entrySource={'result'}
                />

                <DownloadPopup
                    visible={openDownloadPopup}
                    title={_('Descargá tu informe de visita')}
                    onClose={setCloseDownloadPopup}
                    onDownloadBefore={async (f) => {
                        if (f) {
                            return f;
                        }
                        const file = await generateVisitReportPDF({
                            visitReportId: visitReport?.id as number,
                            language,
                        });
                        return file;
                    }}
                    onDownloadAfter={() => {
                        setCloseDownloadPopup();
                    }}
                    beforeDownload={_('Estamos generando el PDF. Una vez listo, podrás descargarlo en unos segundos.')}
                    afterDownload={_(
                        '¡Tu informe de visita ya está listo!\nUna vez descargado, lo vas a encontrar en tu carpeta de Descargas.'
                    )}
                />
                {!simplifiedVersion && (
                    <MoreVertIcon
                        sx={{ color: (t: Theme) => t.palette.primary.onSurface, fontSize: 24, cursor: 'pointer' }}
                        onClick={(e) => {
                            setRowActionsAnchorEl(e.currentTarget);
                        }}
                    />
                )}
                <Menu
                    open={!!rowActionsAnchorEl}
                    onClose={(e) => {
                        e.stopPropagation();
                        setRowActionsAnchorEl(null);
                    }}
                    anchorEl={rowActionsAnchorEl}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                >
                    {menuItems.map((item, index) => (
                        <MenuItem
                            key={index}
                            onClick={(e) => {
                                e.stopPropagation();
                                item.onClick(e);
                                setRowActionsAnchorEl(null);
                            }}
                            disabled={item.disabled}
                        >
                            {item.label}
                        </MenuItem>
                    ))}
                </Menu>
            </Stack>
        </Box>
    );
};

const VisitReportList = ({ visitReports }: { visitReports: VisitReport[] }) => {
    return (
        <Stack direction='column' spacing={1}>
            <Typography variant='subtitle1' color='primary.onSurface' sx={{ fontWeight: 500 }}>
                {_('INFORMES DE VISITA')}
            </Typography>

            <Stack direction='row' flexWrap='wrap' alignItems='start' gap={1}>
                {visitReports?.map((visitReport) => (
                    <VisitReportRow key={visitReport.id} visitReport={visitReport} />
                ))}
            </Stack>
        </Stack>
    );
};

export default VisitReportList;
